import React from 'react';
import clsx from 'clsx';
import {
  ChevronDown,
  ChevronRight,
  Users,
  Edit,
  CalendarPlus,
  UserPlus,
  Plus,
  Minus,
  Info,
  AlertCircle,
  AlertTriangle,
  Eye
} from 'lucide-react';
import Button from '@components/common/Button';
import Badge from '@components/common/Badge';
import MeetingTooltip from '@components/common/MeetingTooltip';
import Tooltip from '@components/common/Tooltip';
import TierBadge from '@components/common/TierBadge';
import DivisionCell from '@components/features/scheduler/DivisionCell';
import InProgressCell from '@components/features/scheduler/InProgressCell';
import { statusToVariant } from '@utils/badgeMappers';
import { ScheduleItem, ScheduleItemContract, TableColumn, AssessmentStatus } from '@app-types';
import { formatAssessmentId } from '@utils/exportUtils';
import { formatShortDate, formatMediumCalendarDate } from '@utils/dateUtils';
import { formatCurrency } from '@utils/currencyUtils';
import { getMeetingStatusLabel } from '@utils/statusUtils';
import { tierToVariant } from '@utils/badgeMappers';
import styles from './AssessorMeetingsTableCells.module.css';

// Get open conflicts for a contract
const getOpenConflicts = (contract: ScheduleItemContract) => {
  return (contract.conflicts || []).filter((c) => c.status !== 'resolved');
};

// Meeting Date Cell Component (matches Scheduler pattern)
export const MeetingDateCell: React.FC<{
  item: ScheduleItem;
  contract: ScheduleItemContract;
  onScheduleDate: (item: ScheduleItem) => void;
  isChildRow?: boolean;
  isGroupRow?: boolean;
}> = ({ item, contract, onScheduleDate, isChildRow, isGroupRow }) => {
  // For child rows, show left-aligned dash
  if (isChildRow) {
    return <span className={styles.emptyCell}>—</span>;
  }

  // For group rows without date
  if (isGroupRow && !item.meeting?.scheduled_datetime) {
    return <span className={styles.noDate}>Not scheduled</span>;
  }

  const showButton = true; // Assessor can always reschedule their meetings

  // Check if any attendee has declined the meeting
  const hasDeclinedAttendee = item.meeting?.invites?.some((invite) => invite.status === 'declined') || false;

  // Prepare meeting data for tooltip with invites array
  const meetingData = item.meeting?.invites
    ? {
        invites: item.meeting.invites
      }
    : null;

  // Prepare contract data for tooltip as array
  const contractsData = [
    {
      assessor_name: item.assessor?.name,
      assessor_id: item.assessor?.id,
      contract_managers: contract?.contract_managers || []
    }
  ];

  return (
    <div className={clsx(styles.meetingDateCell, hasDeclinedAttendee && styles.meetingDeclined)}>
      <div className={styles.roleAssignment}>
        <span className={styles.roleName}>
          {item.meeting?.scheduled_datetime && meetingData ? (
            <>
              <div className={styles.meetingDateWrapper}>
                <MeetingTooltip meeting={meetingData} contracts={contractsData}>
                  <span className={clsx(styles.meetingDate, hasDeclinedAttendee && styles.meetingDateDeclined)}>
                    {formatShortDate(item.meeting.scheduled_datetime)}
                  </span>
                </MeetingTooltip>
                {hasDeclinedAttendee && (
                  <Tooltip
                    content="Some of the attendees have declined the meeting, the reschedule is required"
                    position="bottom"
                    delay={0}
                  >
                    <span className={styles.rescheduleWarningIcon}>
                      <AlertCircle size={16} />
                    </span>
                  </Tooltip>
                )}
              </div>
            </>
          ) : (
            <span className={styles.noDate}>Not scheduled</span>
          )}
        </span>
        {showButton && item.meeting?.status !== 'completed' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onScheduleDate(item)}
            className={styles.roleAssignBtn}
            data-test-id="role-assign-btn-meeting-date"
          >
            {item.meeting?.scheduled_datetime ? <Edit size={14} /> : <CalendarPlus size={14} />}
          </Button>
        )}
      </div>
    </div>
  );
};

// Meeting Status Cell Component
export const MeetingStatusCell: React.FC<{
  isChildRow?: boolean;
  status: string;
}> = ({ isChildRow, status }) => {
  if (isChildRow) {
    return <span className={styles.emptyCell}>—</span>;
  }

  const label = getMeetingStatusLabel(status);

  return <Badge variant={statusToVariant(label as AssessmentStatus)}>{label}</Badge>;
};

// Assessment ID Cell Component with inline create
export const AssessmentIdCell: React.FC<{
  item: ScheduleItem;
  contract: ScheduleItemContract;
  assessmentId: number | null;
  onNavigate: (path: string) => void;
  onCreateAssessment?: (item: ScheduleItem, createForGroup?: boolean, contractId?: number) => void;
  isGroupRow?: boolean;
  isChildRow?: boolean;
  contractsInGroup?: ScheduleItemContract[];
}> = ({
  item,
  contract,
  assessmentId,
  onNavigate,
  onCreateAssessment,
  isGroupRow,
  isChildRow,
  contractsInGroup = []
}) => {
  // For group rows (parent row only, not child rows), check if any contract in the group has an assessment
  if (isGroupRow && !isChildRow && contractsInGroup.length > 0) {
    const hasAnyAssessment = contractsInGroup.some((c) => c.assessment?.id !== null && c.assessment?.id !== undefined);

    if (hasAnyAssessment) {
      // If any contract has assessment, show dash
      return <span className={styles.emptyCell}>—</span>;
    }
    // If no contracts have assessments, show create button (fall through to regular logic)
  }

  // Determine if this is a group row (parent) or child/single row
  const shouldCreateForGroup = isGroupRow && !isChildRow;

  return (
    <div className={styles.assessmentIdCell}>
      {assessmentId ? (
        // Assessment exists - show clickable ID
        <button className={styles.tableLink} onClick={() => onNavigate(`/assessments/${assessmentId}`)}>
          {formatAssessmentId(assessmentId)}
        </button>
      ) : (
        // Assessment doesn't exist - show create button
        <div className={styles.roleAssignment}>
          <span className={styles.roleName}>
            <span className={styles.noDate}>Not Created</span>
          </span>
          {onCreateAssessment && item.meeting?.status !== 'completed' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onCreateAssessment(item, shouldCreateForGroup, contract.id)}
              className={styles.roleAssignBtn}
            >
              <Plus size={14} />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// Assessment Name Cell Component
export const AssessmentNameCell: React.FC<{
  name: string;
}> = ({ name }) => {
  return <span className={styles.tableText}>{name}</span>;
};

// Contract Number Cell Component
export const ContractNumberCell: React.FC<{
  item: ScheduleItem;
  contract: ScheduleItemContract;
  isGroupRow: boolean;
  isChildRow: boolean;
  contractsInGroup: ScheduleItemContract[];
  expandedGroups: Set<number>;
  onToggleExpand: (meetingId: number) => void;
  onNavigate: (path: string) => void;
  onAddToGroup?: (item: ScheduleItem) => void;
  onRemoveFromGroup?: (meetingId: number, contractId: number) => void;
}> = ({
  item,
  contract,
  isGroupRow,
  isChildRow,
  contractsInGroup,
  expandedGroups,
  onToggleExpand,
  onNavigate,
  onAddToGroup,
  onRemoveFromGroup
}) => {
  // Group parent row - show expand button, group badge, and add-to-group button
  if (isGroupRow && !isChildRow) {
    const isExpanded = item.id ? expandedGroups.has(item.id) : false;

    return (
      <div className={styles.statusCellContent}>
        <button
          className={styles.expandButton}
          onClick={(e) => {
            e.stopPropagation();
            if (item.id) onToggleExpand(item.id);
          }}
          title={isExpanded ? 'Collapse' : 'Expand'}
          data-test-id="expand-button"
        >
          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        <span className={styles.groupBadge}>
          <Users size={14} />
          {contractsInGroup.length} contracts
        </span>
        {onAddToGroup && item.meeting?.status !== 'completed' && (
          <button
            className={styles.addToGroupButton}
            data-test-id="add-to-group-button"
            onClick={(e) => {
              e.stopPropagation();
              onAddToGroup(item);
            }}
            title="Add contracts to this group"
          >
            <Plus size={14} />
          </button>
        )}
      </div>
    );
  }

  // Child row - show remove button and contract number
  if (isChildRow && onRemoveFromGroup) {
    return (
      <div className={styles.childStatusContent}>
        {item.meeting?.status !== 'completed' && (
          <button
            className={styles.removeFromGroupButton}
            onClick={(e) => {
              e.stopPropagation();
              if (item.id) onRemoveFromGroup(item.id, contract.id);
            }}
            title="Remove from group"
          >
            <Minus size={14} />
          </button>
        )}
        <button className={styles.contractsTableLink} onClick={() => onNavigate(`/contracts/${contract.id}`)}>
          {contract.contract_number}
        </button>
      </div>
    );
  }

  // Single row - show contract number with add-to-group button
  return (
    <div className={styles.statusCellContent}>
      <button className={styles.contractsTableLink} onClick={() => onNavigate(`/contracts/${contract.id}`)}>
        {contract.contract_number}
      </button>
      {onAddToGroup && item.meeting?.status !== 'completed' && (
        <button
          className={styles.addToGroupButton}
          data-test-id="add-to-group-button"
          onClick={(e) => {
            e.stopPropagation();
            onAddToGroup(item);
          }}
          title="Add contracts to this meeting"
        >
          <Plus size={14} />
        </button>
      )}
    </div>
  );
};

// Supplier Name Cell Component
export const SupplierNameCell: React.FC<{
  item: ScheduleItem;
  contract: ScheduleItemContract;
  isGroupRow: boolean;
  isChildRow: boolean;
  contractsInGroup: ScheduleItemContract[];
  onNavigate: (path: string) => void;
}> = ({ item, contract, isGroupRow, isChildRow, contractsInGroup, onNavigate }) => {
  if (isGroupRow && !isChildRow) {
    const uniqueSuppliers = new Set(contractsInGroup.map((c) => c.supplier_name));
    if (uniqueSuppliers.size > 1) {
      return <span className={styles.contractsTableSupplier}>Multiple Suppliers</span>;
    }
  }

  return (
    <div className={styles.contractsTableSupplier}>
      <button className={styles.contractsTableLink} onClick={() => onNavigate(`/suppliers/${contract.supplier_id}`)}>
        {contract.supplier_name}
      </button>
    </div>
  );
};

// Tier Cell Component
export const TierCell: React.FC<{
  tierName: string;
  scheduleItem?: ScheduleItem;
  isGroupRow?: boolean;
  isChildRow?: boolean;
}> = ({ tierName, scheduleItem, isGroupRow, isChildRow }) => {
  // Check for tier conflicts in the bundle
  let hasBundleTierConflict = false;
  if (scheduleItem) {
    const allConflicts = scheduleItem.contracts.flatMap((c) => getOpenConflicts(c));
    hasBundleTierConflict = allConflicts.some((c) => c.conflict_type_code === 'TIER_MISMATCH');
  }

  // SCENARIO 1 & 2: Parent row in bundle - always show tier(s)
  if (isGroupRow && !isChildRow && scheduleItem) {
    const uniqueTiers = Array.from(new Set(scheduleItem.contracts.map((c) => c.tier)));

    if (uniqueTiers.length > 1) {
      // Scenario 2: Multiple different tiers - show all tiers + warning icon
      return (
        <div className={styles.tierCellWrapper}>
          <div className={styles.tierBadgesGroup}>
            {uniqueTiers.map((tier) => (
              <TierBadge key={`tier-${tier}`} variant={tierToVariant(tier)}>
                {tier}
              </TierBadge>
            ))}
          </div>
          {hasBundleTierConflict && (
            <Tooltip content="Tier conflict" position="top" variant="error">
              <span className={styles.conflictWarningIcon}>
                <AlertTriangle size={16} />
              </span>
            </Tooltip>
          )}
        </div>
      );
    } else {
      // Scenario 1: Single tier - show one tier badge
      return <TierBadge variant={tierToVariant(tierName)}>{tierName}</TierBadge>;
    }
  }

  // SCENARIO 2: Child rows in bundle WITH conflict - show their actual tier
  if (isChildRow && hasBundleTierConflict) {
    return <TierBadge variant={tierToVariant(tierName)}>{tierName}</TierBadge>;
  }

  // SCENARIO 1: Child rows in bundle WITHOUT conflict - show dash
  if (isChildRow) {
    return <span className={styles.emptyCell}>—</span>;
  }

  // SCENARIO 3: Single contract (not in bundle) - show tier badge
  return <TierBadge variant={tierToVariant(tierName)}>{tierName}</TierBadge>;
};

// Assessment Status Cell Component
export const AssessmentStatusCell: React.FC<{
  status: string;
}> = ({ status }) => {
  return <Badge variant={statusToVariant(status as AssessmentStatus)}>{status}</Badge>;
};

// Evidence Status Cell Component
export const EvidenceStatusCell: React.FC<{
  contract: ScheduleItemContract;
}> = ({ contract }) => {
  const evidenceStatus = contract.assessment?.evidence_status;
  if (!evidenceStatus) {
    return <span className={styles.noData}>N/A</span>;
  }
  return <span>{evidenceStatus}</span>;
};

// Evidence Due Date Cell Component
export const EvidenceDueDateCell: React.FC<{
  contract: ScheduleItemContract;
}> = ({ contract }) => {
  const evidenceDueDate = contract.assessment?.evidence_due_date;
  if (!evidenceDueDate) {
    return <span className={styles.noData}>N/A</span>;
  }
  return <span>{formatMediumCalendarDate(evidenceDueDate)}</span>;
};

// Contract Managers Cell Component
export const ContractManagersCell: React.FC<{
  item: ScheduleItem;
  contract: ScheduleItemContract;
  managers: Array<{ id: number; name: string }>;
  isChildRow?: boolean;
  isGroupRow?: boolean;
  onAssignRole?: (item: ScheduleItem, contractId: number, role: 'owner' | 'manager' | 'assessor') => void;
}> = ({ item, contract, managers, isChildRow, isGroupRow, onAssignRole }) => {
  const openConflicts = isGroupRow && !isChildRow
    ? item.contracts.flatMap((c) => getOpenConflicts(c))
    : getOpenConflicts(contract);
  const managerConflicts = openConflicts.filter((c) => c.conflict_type_code === 'CM_MISMATCH');
  const hasManagerConflict = managerConflicts.length > 0;

  let displayManagers = managers;
  if (isGroupRow && !isChildRow && hasManagerConflict) {
    const allManagers = item.contracts.flatMap((c) => c.contract_managers);
    displayManagers = Array.from(new Map(allManagers.map((m) => [m.id, m])).values());
  }

  const hasManagers = displayManagers.length > 0;
  let displayText = 'Not Assigned';
  if (hasManagers) {
    displayText = displayManagers.length === 1
      ? displayManagers[0].name
      : `${displayManagers[0].name} +${displayManagers.length - 1}`;
  }

  if (isChildRow) {
    return <span className={styles.emptyCell}>—</span>;
  }

  return (
    <div className={styles.managerCell}>
      <div className={styles.roleAssignment}>
        <span className={styles.roleName} title={hasManagers ? displayManagers.map((m) => m.name).join(', ') : undefined}>
          {hasManagers ? displayText : <span className={styles.noDate}>Not Assigned</span>}
        </span>
        {onAssignRole && item.meeting?.status !== 'completed' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onAssignRole(item, contract.id, 'manager')}
            className={styles.roleAssignBtn}
            data-test-id="role-assign-btn-manager"
          >
            {hasManagers ? <Edit size={14} /> : <UserPlus size={14} />}
          </Button>
        )}
      </div>
      {hasManagerConflict && !isChildRow && (
        <Tooltip
          content={managerConflicts.length === 1 ? managerConflicts[0].conflict_type_name : `${managerConflicts.length} manager conflicts`}
          position="top"
          variant="error"
        >
          <span className={styles.conflictWarningIcon}>
            <AlertTriangle size={16} />
          </span>
        </Tooltip>
      )}
    </div>
  );
};

// Contract Owners Cell Component
export const ContractOwnersCell: React.FC<{
  item: ScheduleItem;
  contract: ScheduleItemContract;
  owners: Array<{ id: number; name: string }>;
  isChildRow?: boolean;
  isGroupRow?: boolean;
  onAssignRole?: (item: ScheduleItem, contractId: number, role: 'owner' | 'manager' | 'assessor') => void;
}> = ({ item, contract, owners, isChildRow, isGroupRow, onAssignRole }) => {
  const hasOwners = owners.length > 0;
  let displayText = 'Not Assigned';
  if (hasOwners) {
    displayText = owners.length === 1
      ? owners[0].name
      : `${owners[0].name} +${owners.length - 1}`;
  }

  // Unlike managers, owners are shown for ALL rows (group, child, single)
  // No dash logic needed here

  return (
    <div className={styles.ownerCell}>
      <div className={styles.roleAssignment}>
        <span className={styles.roleName} title={hasOwners ? owners.map((o) => o.name).join(', ') : undefined}>
          {hasOwners ? (
            displayText
          ) : (
            <span className={clsx(styles.noDate, styles.missingOwner)}>
              Not Assigned
              <Tooltip
                content="CO not required for meeting, but needed for accurate division reporting"
                position="top"
                variant="warning"
                gap={36}
              >
                <span className={styles.infoIcon}>
                  <Info size={18} />
                </span>
              </Tooltip>
            </span>
          )}
        </span>
        {onAssignRole && item.meeting?.status !== 'completed' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onAssignRole(item, contract.id, 'owner')}
            className={styles.roleAssignBtn}
            data-test-id="role-assign-btn-owner"
          >
            {hasOwners ? <Edit size={14} /> : <UserPlus size={14} />}
          </Button>
        )}
      </div>
    </div>
  );
};

// Assessor Cell Component
const AssessorCell: React.FC<{
  item: ScheduleItem;
  contract: ScheduleItemContract;
  isGroupRow: boolean;
  isChildRow: boolean;
  onAssignRole?: (item: ScheduleItem, contractId: number, role: 'owner' | 'manager' | 'assessor') => void;
}> = ({ item, contract, isGroupRow, isChildRow, onAssignRole }) => {
  const hasAssessor = !!item.assessor?.name;

  return (
    <div className={styles.assessorCell}>
      <div className={styles.roleAssignment}>
        <span className={styles.roleName}>
          {(() => {
            if (isChildRow) return <span className={styles.emptyCell}>—</span>;
            if (hasAssessor) return item.assessor?.name;
            return <span className={styles.noDate}>Not Assigned</span>;
          })()}
        </span>
        {!isChildRow && onAssignRole && item.meeting?.status !== 'completed' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onAssignRole(item, contract.id, 'assessor')}
            className={styles.roleAssignBtn}
            data-test-id="role-assign-btn-assessor"
          >
            {hasAssessor ? <Edit size={14} /> : <UserPlus size={14} />}
          </Button>
        )}
      </div>
    </div>
  );
};

// Main Cell Renderer
export const AssessorMeetingsCellRenderer = ({
  column,
  item,
  contract,
  isGroupRow = false,
  isChildRow = false,
  contractsInGroup = [],
  expandedGroups,
  onToggleExpand,
  onNavigate,
  onCreateAssessment,
  onScheduleDate,
  onAssignRole,
  onAddToGroup,
  onRemoveFromGroup
}: {
  column: TableColumn;
  item: ScheduleItem;
  contract: ScheduleItemContract;
  isGroupRow?: boolean;
  isChildRow?: boolean;
  contractsInGroup?: ScheduleItemContract[];
  expandedGroups: Set<number>;
  onToggleExpand: (meetingId: number) => void;
  onNavigate: (path: string) => void;
  onCreateAssessment?: (item: ScheduleItem, createForGroup?: boolean, contractId?: number) => void;
  onScheduleDate?: (item: ScheduleItem) => void;
  onAssignRole?: (item: ScheduleItem, contractId: number, role: 'owner' | 'manager' | 'assessor') => void;
  onAddToGroup?: (item: ScheduleItem) => void;
  onRemoveFromGroup?: (meetingId: number, contractId: number) => void;
}): React.ReactElement => {
  // For group rows, limit displayed columns
  const allowedGroupColumns = [
    'meeting_date',
    'meeting_status',
    'contract_number',
    'supplier_name',
    'tier',
    'division',
    'assessor',
    'contract_managers',
    'assessment_id',
    'in_progress',
    'view'
  ];

  if (isGroupRow && !isChildRow && !allowedGroupColumns.includes(column.key)) {
    return <span className={styles.emptyCell}>—</span>;
  }

  // Get meeting status from meeting object
  const getMeetingStatus = (): string => {
    return item.meeting?.status || 'Not Scheduled';
  };

  switch (column.key) {
    case 'meeting_date':
      return (
        <MeetingDateCell
          item={item}
          contract={contract}
          onScheduleDate={onScheduleDate || (() => {})}
          isChildRow={isChildRow}
          isGroupRow={isGroupRow}
        />
      );

    case 'meeting_status':
      return <MeetingStatusCell isChildRow={isChildRow} status={getMeetingStatus()} />;

    case 'assessment_id':
      return (
        <AssessmentIdCell
          item={item}
          contract={contract}
          assessmentId={contract.assessment?.id || null}
          onNavigate={onNavigate}
          onCreateAssessment={onCreateAssessment}
          isGroupRow={isGroupRow}
          isChildRow={isChildRow}
          contractsInGroup={contractsInGroup}
        />
      );

    case 'assessment_name':
      return <AssessmentNameCell name={contract.assessment?.name || ''} />;

    case 'contract_number':
      return (
        <ContractNumberCell
          item={item}
          contract={contract}
          isGroupRow={isGroupRow}
          isChildRow={isChildRow}
          contractsInGroup={contractsInGroup}
          expandedGroups={expandedGroups}
          onToggleExpand={onToggleExpand}
          onNavigate={onNavigate}
          onAddToGroup={onAddToGroup}
          onRemoveFromGroup={onRemoveFromGroup}
        />
      );

    case 'supplier_name':
      return (
        <SupplierNameCell
          item={item}
          contract={contract}
          isGroupRow={isGroupRow}
          isChildRow={isChildRow}
          contractsInGroup={contractsInGroup}
          onNavigate={onNavigate}
        />
      );

    case 'tier':
      return <TierCell tierName={contract.tier} scheduleItem={item} isGroupRow={isGroupRow} isChildRow={isChildRow} />;

    case 'division':
      return <DivisionCell scheduleItem={item} contract={contract} isGroupRow={isGroupRow} isChildRow={isChildRow} />;

    case 'assessor':
      return (
        <AssessorCell
          item={item}
          contract={contract}
          isGroupRow={isGroupRow}
          isChildRow={isChildRow}
          onAssignRole={onAssignRole}
        />
      );

    case 'assessment_status':
      return <AssessmentStatusCell status={contract.assessment?.status || 'Not Created'} />;

    case 'evidence_status':
      return <EvidenceStatusCell contract={contract} />;

    case 'evidence_due_date':
      return <EvidenceDueDateCell contract={contract} />;

    case 'contract_name':
      return <span>{contract.name || '-'}</span>;

    case 'contract_start_date':
      return <span>{formatMediumCalendarDate(contract.start_date)}</span>;

    case 'contract_end_date':
      return <span>{formatMediumCalendarDate(contract.end_date)}</span>;

    case 'contract_amount':
      return <span>{formatCurrency(contract.amount)}</span>;

    case 'contract_type':
      return <span>{contract.contract_type || '-'}</span>;

    case 'cost_center':
      return <span>{contract.cost_center || '-'}</span>;

    case 'in_progress':
      return (
        <InProgressCell scheduleItem={item} isGroupRow={Boolean(isGroupRow)} isChildRow={Boolean(isChildRow)} />
      );

    case 'scheduling_notes':
      return <span>{item.meeting?.notes || '-'}</span>;

    case 'rescheduled_date':
      return <span>{formatMediumCalendarDate(item.meeting?.previous_scheduled_datetime)}</span>;

    case 'rescheduled_reason':
      return <span>{item.meeting?.reschedule_reason || '-'}</span>;

    case 'contract_managers':
      return (
        <ContractManagersCell
          item={item}
          contract={contract}
          managers={contract.contract_managers || []}
          isChildRow={isChildRow}
          isGroupRow={isGroupRow}
          onAssignRole={onAssignRole}
        />
      );

    case 'contract_owners':
      return (
        <ContractOwnersCell
          item={item}
          contract={contract}
          owners={contract.contract_owners || []}
          isChildRow={isChildRow}
          isGroupRow={isGroupRow}
          onAssignRole={onAssignRole}
        />
      );

    case 'view':
      // Only show view button for parent row (group) or single row with a meeting ID
      if (isChildRow || !item.id) {
        return <span></span>;
      }
      return (
        <div className={styles.actionsCell} title="View meeting details">
          <Button
            variant="ghost"
            size="xs"
            onClick={() => onNavigate(`/contract-assessment-meetings/${item.id}`)}
            className={styles.viewMeetingBtn}
          >
            <Eye size={18} />
          </Button>
        </div>
      );

    default:
      return <span>-</span>;
  }
};
